import React, { useCallback, useMemo, useState } from "react";
import { pluralize } from "@common/helpers/string";
import { BankCashItemType } from "@common/types/bankCashItem";
import { ImporterDrawerShell } from "@components/importer/ImporterDrawerShell";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getApiErrorMessage } from "@api/apiError";
import { useCreateCashItemsMutation } from "@api/cashItems.api";
import { useFinancialAccountsById } from "@hooks/useFinancialAccountsById";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { cashItemTypeOf } from "./cashItemAmount";
import { CashItemImporterSpreadsheet } from "./CashItemImporterSpreadsheet";
import {
    applyCashItemBatchResults,
    CashItemImporterRow,
    cashItemImporterFields,
    createCashItemImporterRow,
    isBlankCashItemRow,
} from "./cashItemImporterRows";
import {
    createCashItemRowSchema,
    InvalidCellsByRowId,
    validateCashItemImport,
} from "./cashItemImportValidation";

const TITLE = "Import checks and deposits";

const NO_INVALID_CELLS: InvalidCellsByRowId = new Map();

interface InvalidCells {
    rows: CashItemImporterRow[];
    cellsByRowId: InvalidCellsByRowId;
}

interface Props {
    open?: boolean;
    onClose: () => void;
    initialFinancialAccountId?: number;
}

export const CashItemImporterDrawer: React.FC<Props> = ({
    open = true,
    onClose,
    initialFinancialAccountId,
}) => {
    const { enabled: isGlFirst } = useIsGlFirst();
    const financialAccountsById = useFinancialAccountsById();
    const { toast, error: showError } = useToaster();
    const { mutateAsync: createCashItems, isPending } =
        useCreateCashItemsMutation();

    const [rows, setRows] = useState<CashItemImporterRow[]>(() => [
        createCashItemImporterRow(
            initialFinancialAccountId == null
                ? {}
                : { financialAccountId: initialFinancialAccountId },
        ),
    ]);

    const [invalidCells, setInvalidCells] = useState<InvalidCells | null>(null);
    const invalidCellsByRowId =
        invalidCells?.rows === rows
            ? invalidCells.cellsByRowId
            : NO_INVALID_CELLS;

    const rowSchema = useMemo(
        () => createCashItemRowSchema({ isGlFirst, financialAccountsById }),
        [isGlFirst, financialAccountsById],
    );

    const filledRows = useMemo(
        () => rows.filter((row) => !isBlankCashItemRow(row)),
        [rows],
    );
    const checkCount = filledRows.filter(
        (row) => cashItemTypeOf(row.amount) === BankCashItemType.CHECK,
    ).length;
    const depositCount = filledRows.filter(
        (row) => cashItemTypeOf(row.amount) === BankCashItemType.DEPOSIT,
    ).length;

    const handleSubmit = useCallback(async () => {
        if (filledRows.length === 0) {
            showError("Enter at least one check or deposit");
            return;
        }

        const validation = validateCashItemImport({ rows, rowSchema });
        if (!validation.isValid) {
            setInvalidCells({
                rows,
                cellsByRowId: validation.invalidCellsByRowId,
            });
            showError(validation.message);
            return;
        }

        const { entries } = validation;
        const response = await createCashItems(
            entries.map(({ item }) => item),
        ).catch((error: Error) => {
            showError(
                getApiErrorMessage(
                    error,
                    "Failed to import checks and deposits",
                ),
            );
            return null;
        });
        if (!response) {
            return;
        }

        const { succeeded, failed } = response.summary;
        const importedMessage = `Imported ${succeeded} ${pluralize("item", succeeded)}`;
        if (failed === 0) {
            toast(importedMessage);
            onClose();
            return;
        }

        const { remainingRows, failures } = applyCashItemBatchResults({
            rows,
            entries,
            results: response.results,
        });
        setRows(remainingRows);
        setInvalidCells({
            rows: remainingRows,
            cellsByRowId: new Map(
                failures.map(({ rowId }) => [
                    rowId,
                    new Set(cashItemImporterFields),
                ]),
            ),
        });
        const [firstFailure] = failures;
        const firstFailureMessage = firstFailure
            ? ` Row ${firstFailure.rowNumber}: ${firstFailure.message}`
            : "";
        showError(
            succeeded === 0
                ? `Failed to import ${failed} ${pluralize("item", failed)}.${firstFailureMessage}`
                : `${importedMessage}, ${failed} failed.${firstFailureMessage}`,
        );
    }, [
        filledRows,
        rows,
        rowSchema,
        createCashItems,
        toast,
        showError,
        onClose,
    ]);

    return (
        <ImporterDrawerShell
            title={TITLE}
            open={open}
            onClose={onClose}
            statusItems={[
                `${checkCount} ${pluralize("check", checkCount)}`,
                `${depositCount} ${pluralize("deposit", depositCount)}`,
            ]}
            submitLabel={TITLE}
            submitHotkeyDescription={TITLE}
            isSubmitting={isPending}
            onSubmit={handleSubmit}
        >
            <CashItemImporterSpreadsheet
                rows={rows}
                setRows={setRows}
                financialAccountsById={financialAccountsById}
                invalidCellsByRowId={invalidCellsByRowId}
            />
        </ImporterDrawerShell>
    );
};
